import PropTypes from 'prop-types';

/*
You can find the definition to all these events here:
https://docs.google.com/spreadsheets/d/1x138LIQssTbjO0YDP7Jnz3d2hZtMuMmalkQ-_oTIVp4/edit#gid=367769533
If you add a new event make sure to add it to the document as well and describe what will trigger the event and any properties the event has.
Make sure you follow the naming standard when adding new events.
 */

const FLOW_TYPES = PropTypes.oneOf(['gift', 'subscription', 'one time']);
const SUPPORT_TYPES = PropTypes.oneOf(['one time', 'monthly']);
const SIGNING_METHODS = PropTypes.oneOf(['password', 'facebook', 'google', 'apple', 'anonymous']);

export const ADJUST_EVENTS = {
  oneTimeDonationSuccessful: { token: 'g7kcen', name: 'one-time donation successful' },
  giftPurchaseSuccessful: { token: 'es1lp0', name: 'gift purchase successful' },
  webSessionStart: { token: 'losfal', name: 'web session start' },
  clickAppDownload: { token: 'xy4pqp', name: 'click app download' },
  subscriptionPurchaseSuccessful: { token: 'r3hzvv', name: 'subscription purchase successful' },
};

type AnalyticsEvent = { name: string; properties?: Record<string, unknown> };

export const EVENTS: { [key: string]: AnalyticsEvent } = {
  supportSuccessful: {
    name: 'support - support successful',
    properties: {
      'category id': PropTypes.string,
      'impacter ids': PropTypes.array,
      'impacter names': PropTypes.array,
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      'payment provider': PropTypes.oneOf(['stripe', 'klarna']),
      'payment method': PropTypes.oneOf(['card', 'apple pay']),
      'tag names': PropTypes.array,
    },
  },
  supportFailed: {
    name: 'support - support failed',
    properties: {
      'impacter ids': PropTypes.array,
      'impacter names': PropTypes.array,
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      'payment provider': PropTypes.oneOf(['stripe', 'klarna']),
      'payment method': PropTypes.oneOf(['card', 'apple pay']),
      'tag names': PropTypes.array,
      error: PropTypes.any,
    },
  },
  supportClickCancel: {
    name: 'support - click cancel',
    properties: {
      'impacter ids': PropTypes.array,
      'impacter names': PropTypes.array,
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      'payment provider': PropTypes.oneOf(['stripe', 'klarna']),
      'tag names': PropTypes.array,
    },
  },
  supportClickSupport: {
    name: 'support - click support',
    properties: {
      initiator: PropTypes.string,
      'impacter ids': PropTypes.arrayOf(PropTypes.number),
      'impacter names': PropTypes.arrayOf(PropTypes.string),
      'project id': PropTypes.number,
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      'tag names': PropTypes.array,
    },
  },
  supportClickConfirm: {
    name: 'support - click confirm support',
    properties: {
      'impacter ids': PropTypes.array,
      'impacter names': PropTypes.array,
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      'payment provider': PropTypes.oneOf(['stripe', 'klarna']),
      'payment method': PropTypes.oneOf(['card', 'apple pay']),
      'tag names': PropTypes.array,
    },
  },
  supportChangePeriod: {
    name: 'support - change period',
    properties: {
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      period: PropTypes.number,
      'currency code': PropTypes.string,
    },
  },
  supportClickConfirmPeriod: {
    name: 'support - click confirm period',
    properties: {
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      period: PropTypes.number,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
    },
  },
  supportClickConfirmReceiverInformation: {
    name: 'support - click confirm receiver information',
    properties: {
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
      hasMessage: PropTypes.bool,
      'currency code': PropTypes.string,
    },
  },
  supportEnterThankYouPage: {
    name: 'support - enter thank you page',
    properties: {
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
    },
  },
  supportChangePaymentType: {
    name: 'support - change payment type',
    properties: {
      campaign: PropTypes.string,
      initiator: PropTypes.string,
      'payment flow id': PropTypes.string,
      'payment provider': PropTypes.oneOf(['stripe', 'klarna']),
      'payment type': PropTypes.string,
    },
  },
  paymentManagementAddCard: {
    name: 'payment management - add card',
    properties: {
      'payment provider': PropTypes.oneOf(['stripe']),
      'payment method': PropTypes.oneOf(['card']),
      'payment flow id': PropTypes.string,
    },
  },
  paymentManagementChangeDefaultCard: {
    name: 'payment management - change default card',
    properties: {
      'payment provider': PropTypes.oneOf(['stripe']),
      'payment method': PropTypes.oneOf(['card']),
      'payment flow id': PropTypes.string,
    },
  },
  paymentManagementClickDeleteCard: {
    name: 'payment management - click delete card',
    properties: {
      'payment provider': PropTypes.oneOf(['stripe']),
      'payment method': PropTypes.oneOf(['card']),
      'payment flow id': PropTypes.string,
    },
  },
  viewHomePage: {
    name: 'home - view home page',
    properties: {
      'payment flow id': PropTypes.string,
    },
  },
  viewCategory: {
    name: 'view category',
    properties: {
      'category name': PropTypes.string,
      'impacter ids': PropTypes.array,
      'impacter names': PropTypes.array,
      'payment flow id': PropTypes.string,
    },
  },
  viewCampaign: {
    name: 'view campaign',
    properties: {
      'campaign name': PropTypes.oneOf(['take-action', 'bee-the-change', 'tomorrow-tree']),
      'impacter ids': PropTypes.array,
      'impacter names': PropTypes.array,
      tags: PropTypes.array,
      'project id': PropTypes.number,
      'payment flow id': PropTypes.string,
      country: PropTypes.string,
      language: PropTypes.string,
      referrer: PropTypes.string,
      'utm source': PropTypes.string,
    },
  },
  viewImpacter: {
    name: 'view impacter',
    properties: {
      'impacter id': PropTypes.number,
      'impacter name': PropTypes.string,
    },
  },
  viewBlogPost: {
    name: 'view blog post',
    properties: {
      title: PropTypes.string,
      slug: PropTypes.string,
    },
  },
  viewDonatePage: {
    name: 'home - view donate page',
    properties: {},
  },
  viewConfirmDonationPage: {
    name: 'home - view confirm donation page',
    properties: {},
  },
  clickAppDownload: {
    name: 'click app download',
    properties: {
      initiator: PropTypes.oneOf(['nav bar', 'get the app section', 'thank you page', 'app page']),
      'store name': PropTypes.oneOf(['app store', 'play store']),
    },
  },
  clickShareLink: {
    name: 'share - click share link',
    properties: {
      initiator: PropTypes.oneOf(['thank you page']),
      link: PropTypes.string,
      campaign: PropTypes.oneOf(['bee-the-change', 'take-action']),
    },
  },
  shareLinkSuccess: {
    name: 'share - share link success',
    properties: {
      initiator: PropTypes.oneOf(['thank you page']),
      link: PropTypes.string,
      campaign: PropTypes.oneOf(['bee-the-change', 'take-action']),
    },
  },
  shareLinkCancel: {
    name: 'share - share link cancel',
    properties: {
      initiator: PropTypes.oneOf(['thank you page']),
      link: PropTypes.string,
      campaign: PropTypes.oneOf(['bee-the-change', 'take-action']),
    },
  },
  shareBlogPost: {
    name: 'share blog post',
    properties: {
      title: PropTypes.string,
      slug: PropTypes.string,
      platform: PropTypes.string,
    },
  },
  signUpViewPage: {
    name: 'sign up - view sign up page',
    properties: {
      initiator: PropTypes.string,
    },
  },
  signUpClickSignUp: {
    name: 'sign up - click sign up button',
    properties: {
      validation: PropTypes.oneOf(['passed', 'failed']),
    },
  },
  signUpAccountCreated: {
    name: 'sign up - account created',
    properties: {
      method: SIGNING_METHODS,
    },
  },
  signUpAccountFailed: {
    name: 'sign up - account failed',
    properties: {
      method: SIGNING_METHODS,
      error: PropTypes.object,
    },
  },
  loginViewPage: {
    name: 'login - view login page',
    properties: {
      initiator: PropTypes.string,
    },
  },
  loginClickPasswordLoginButton: {
    name: 'login - click password login button',
  },
  loginLogin: {
    name: 'login - login',
    properties: {
      method: SIGNING_METHODS,
    },
  },
  loginLoginFailed: {
    name: 'login - login failed',
    properties: {
      method: SIGNING_METHODS,
      error: PropTypes.object,
    },
  },

  logout: {
    name: 'logout',
  },

  giftingViewGift: {
    name: 'gifting - view gift page',
  },

  giftingViewBuyGift: {
    name: 'gifting - view buy gift page',
    properties: {
      'payment flow id': PropTypes.string,
      'support type': PropTypes.string,
      'flow type': PropTypes.string,
    },
  },

  giftingViewRedeem: {
    name: 'gifting - view redeem page',
    properties: {
      'gift id': PropTypes.string,
    },
  },
  giftingViewRedeemFlow: {
    name: 'gifting - view redeem flow page',
    properties: {
      'redeem flow id': PropTypes.string,
      'gift id': PropTypes.string,
    },
  },
  giftingClickConfirmCategory: {
    name: 'gifting - click confirm category',
    properties: {
      'redeem flow id': PropTypes.string,
      'gift id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      period: PropTypes.number,
      'category id': PropTypes.string,
      'category name': PropTypes.string,
      tags: PropTypes.array,
    },
  },
  giftingClickConfirmRedeemGift: {
    name: 'gifting - click confirm gift redemption',
    properties: {
      'redeem flow id': PropTypes.string,
      'gift id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      period: PropTypes.number,
      'category id': PropTypes.string,
      'category name': PropTypes.string,
      tags: PropTypes.array,
    },
  },
  giftingRedeemGiftSuccessful: {
    name: 'gifting - redeem gift successful',
    properties: {
      'redeem flow id': PropTypes.string,
      'gift id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      period: PropTypes.number,
      'category id': PropTypes.string,
      'category name': PropTypes.string,
      tags: PropTypes.array,
    },
  },
  giftingRedeemGiftFailed: {
    name: 'gifting - redeem gift failed',
    properties: {
      'redeem flow id': PropTypes.string,
      amount: PropTypes.number,
      'currency code': PropTypes.string,
      period: PropTypes.number,
      'category id': PropTypes.string,
      'category name': PropTypes.string,
      tags: PropTypes.array,
      error: PropTypes.object,
    },
  },
  giftingViewRedeemThankYou: {
    name: 'gifting - view redeem gift thank you',
    properties: {
      'redeem flow id': PropTypes.string,
    },
  },
  webSessionStart: {
    name: 'web - session start',
    properties: {},
  },
  buttonClick: {
    name: 'click button',
    properties: {
      name: PropTypes.string,
      'from path': PropTypes.string,
      'from query': PropTypes.string,
      to: PropTypes.string,
    },
  },
  firebaseSigninRedirectSuccessful: {
    name: 'firebase - sign-in redirect successful',
  },
  firebaseSigninRedirectFailed: {
    name: 'firebase - sign-in redirect failed',
  },
  firebaseAccountCreatedSuccessful: {
    name: 'firebase - account created successful',
    properties: {
      method: SIGNING_METHODS,
    },
  },
  firebaseAccountCreatedFailed: {
    name: 'firebase - account created failed',
    properties: {
      method: SIGNING_METHODS,
      error: PropTypes.object,
    },
  },
  firebaseAuthStateChanged: {
    name: 'firebase - auth state changed',
    properties: {
      signedIn: PropTypes.bool,
    },
  },
  navigation: {
    name: 'navigation',
    properties: {
      'from path': PropTypes.string,
      'from query': PropTypes.string,
      to: PropTypes.string,
    },
  },
  viewCreateAccount: {
    name: 'view create account',
  },
  viewEnterPassword: {
    name: 'view enter password',
  },
  viewPayoutOverview: {
    name: 'impacter tools - view payout overview',
  },
  viewSinglePayout: {
    name: 'impacter tools - view single payout',
  },
  viewSupportOverview: {
    name: 'impacter tools - view support overview',
  },
  viewMonthFinancialDetails: {
    name: 'impacter tools - view month financial details',
  },
  downloadFile: {
    name: 'impacter tools - download file',
    properties: {
      initiator: PropTypes.string,
      fileType: PropTypes.string,
    },
  },
  startPaymentFlow: {
    name: 'support - start payment flow',
    properties: {
      initiator: PropTypes.string,
      'impacter ids': PropTypes.arrayOf(PropTypes.number),
      'impacter names': PropTypes.arrayOf(PropTypes.string),
      'project id': PropTypes.number,
      'support type': SUPPORT_TYPES,
      'flow type': FLOW_TYPES,
      'payment flow id': PropTypes.string,
    },
  },
};
